import { PlayerIdentity } from "components/ReviewAudit/PublicRatings"
import React, { useContext } from "react"
import { Badge } from "reactstrap"
import { Link } from "react-router-dom"
import { getParticipantEntryCount, getPlayerEntryCount } from "../../../../eventEntryCounts"
import { getParticipantGameLevel } from "./participantLevel"
import { distanceToEvent, participantAge, participantLocation, withEntryDetails } from "./participantDisplay"
import { DrawPlayerContext } from "./DrawPlayerDetails"
import { accountNumber } from "./participantIdentity"
import CopyContact from "components/Common/CopyContact"
import { placeLabel } from "../Tabs/Entries/Tenis10ChildEntryModal"
import "./participantSummary.scss"

const name = person => [person?.firstName, person?.lastName].filter(Boolean).join(" ") || "Participant unavailable"
export const participantNumber = value => (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) && Number.isFinite(Number(value)) ? Number(value) : null
export const participantStatistic = (person, key, gameType) => participantNumber(
  (gameType === "doubles" ? person?.[`${key}Double`] : undefined) ??
  person?.[key]?.[gameType] ??
  (typeof person?.[key] !== "object" ? person?.[key] : undefined) ??
  (key === "points" ? person?.userPoints : undefined)
)
export const entryFee = (amount, currency) => participantNumber(amount) !== null && currency ? `${participantNumber(amount)} ${currency}` : "Unknown"
const Fact = ({ label, children }) => <div><dt>{label}</dt><dd>{children ?? "N/A"}</dd></div>
const Seed = ({ rank }) => rank != null ? <>{" "}<span className="main-draw-seed" aria-label={`Seed ${rank}`}>[{rank}]</span></> : null
const Contact = ({ value, label, copy }) => copy && value
  ? <CopyContact key={`${label}:${value}`} value={value} label={label} />
  : <span>{value || (label === "email" ? "Email unavailable" : "Phone unavailable")}</span>

// PRD 16 LR11b (register E39 (1)): a Tenis10 child (profileId) — its name; its parent only as the contact person (the admin reads the
// real data, ADR-0003 / E27). Never the parent's account, level, rank, plan or place as the child's. Its own entries in the event and
// its own W (PRD 16 LR11c), never its parent's or a sibling's. Its own number (RELEASE-TENIS10 § 11 P78). No level or category: every
// child of a Tenis10 competition has the competition's colour and age category (RELEASE-TENIS10 § 11 P90, Tudor 2026-10-26).
// P73 (Tudor 2026-10-29): where it lives, written as Add entry writes it — on its Entries row and on its draw cards (withChildPlace).
const ChildParticipant = ({ person, currency, eventDetails, showContacts, copyContacts, showFee, seedRank, compact }) => {
  const parent = person.contact
  const count = getParticipantEntryCount(eventDetails, person)
  const place = placeLabel(person.place)
  return <div className="entries-participant" role="group" aria-label={name(person)}>
    <div className="entries-participant-heading">
      <PlayerIdentity player={person} to={null} className="font-weight-bold">{name(person)}<Seed rank={seedRank} /></PlayerIdentity>
      {/* RELEASE-TENIS10 § 11 P78: the child's own number where an adult's #userId sits (as an adult's without an account) */}
      {accountNumber(person) != null && <span className="text-muted">#{accountNumber(person)}</span>}
      {count > 1 && <Badge color="primary" title={`${count} entries in this event`} aria-label={`${count} entries in this event`}>{count}</Badge>}
      {person.w === true && <Badge color="success" title="Confirmed entry in another event in the same week" aria-label="Confirmed entry in another event in the same week">W</Badge>}
      {person.unclaimed === true && <Badge color="warning" title="Held by its tenis10.ro family until a parent claims it">Not claimed yet</Badge>}
    </div>
    {place && <div className="entries-locality" data-testid="child-place">
      <i className="bx bx-map mr-1" aria-hidden="true" /><span className="sr-only">Location: </span>{place}
    </div>}
    {showContacts && parent && <div className="entries-contact text-muted">
      <span>{person.unclaimed === true ? "tenis10.ro parent" : "Parent"}: {name(parent)}{" "}
        {parent.id ? <Link to={`/accounts/players/${parent.id}`} target="_blank" rel="noopener noreferrer">#{parent.userId ?? "—"}</Link>
          : person.unclaimed === true ? null : <>#{parent.userId ?? "—"}</>}</span>
      {!compact && <Contact value={parent.email} label="email" copy={copyContacts} />}
      <Contact value={parent.phone} label="phone" copy={copyContacts} />
    </div>}
    {!compact && <dl className="entries-player-facts">
      {showFee && <Fact label="SSF">{entryFee(person.registrationFee, currency)}</Fact>}
      {person.groupName && person.groupPosition != null && <Fact label="Group position">{`${person.groupName}${person.groupPosition}`}</Fact>}
    </dl>}
  </div>
}

const Participant = ({ person, fallbackId, gameType, currency, eventDetails, showContacts = true, copyContacts = false, showFee = true, seedRank, variant }) => {
  const players = useContext(DrawPlayerContext)
  if (person?.profileId) return <ChildParticipant person={person} currency={currency} eventDetails={eventDetails} showContacts={showContacts}
    copyContacts={copyContacts} showFee={showFee} seedRank={seedRank} compact={variant === "draw"} />
  const profile = players[person?.userId]
  if (profile) person = { ...withEntryDetails(person, profile), location: profile.location || person.location }
  const userId = person?.userId ?? fallbackId
  const profileId = person?.id || person?._id
  const count = getPlayerEntryCount(eventDetails, userId)
  const suffix = gameType === "doubles" ? "D" : "S"
  const compact = variant === "draw"
  const { city, country } = participantLocation(person?.location)
  const age = participantAge(person?.birthDate)
  const distance = distanceToEvent(person?.location, eventDetails)
  return <div className="entries-participant" role="group" aria-label={name(person)}>
    <div className="entries-participant-heading">
      <PlayerIdentity player={{ ...person, userId }} to={null} className="font-weight-bold">{name(person)}<Seed rank={seedRank} /></PlayerIdentity>
      {profileId ? <Link to={`/accounts/players/${profileId}`} target="_blank" rel="noopener noreferrer">#{userId ?? "—"}</Link> : <span className="text-muted">#{userId ?? "—"}</span>}
      {count > 1 && <Badge color="primary" title={`${count} entries in this event`} aria-label={`${count} entries in this event`}>{count}</Badge>}
      {person?.w === true && <Badge color="success" title="Confirmed entry in another event in the same week" aria-label="Confirmed entry in another event in the same week">W</Badge>}
    </div>
    <div className="entries-locality">
      {compact && <><span>Lvl-{suffix}: {getParticipantGameLevel(person, gameType) ?? "N/A"}</span>{" · "}</>}
      {age !== null && <><span title="Age by calendar year">{age} years</span>{" · "}</>}
      <span title={country || undefined}>{city}</span>
      {distance !== null && <>{" · "}<span title="Approximate straight-line distance to the venue">~{distance} km</span></>}
    </div>
    {showContacts && <div className="entries-contact text-muted">
      {!compact && <Contact value={person?.email} label="email" copy={copyContacts} />}
      <Contact value={person?.phone} label="phone" copy={copyContacts} />
    </div>}
    {!compact && <dl className="entries-player-facts">
      <Fact label={`Rank-${suffix}`}>{participantStatistic(person, "rank", gameType) ?? "N/A"} ({participantStatistic(person, "points", gameType) ?? "N/A"} pts)</Fact>
      <Fact label={`Lvl-${suffix}`}>{getParticipantGameLevel(person, gameType)}</Fact>
      <Fact label="Plan"><span className="text-capitalize">{person?.membership?.plan || "N/A"}</span></Fact>
      {showFee && <Fact label="SSF">{entryFee(person?.registrationFee, currency)}</Fact>}
      {person?.groupName && person?.groupPosition != null && <Fact label="Group position">{`${person.groupName}${person.groupPosition}`}</Fact>}
    </dl>}
  </div>
}

export default Participant
